import { useEffect, useState, useRef } from "react";
import { Modal, Form, Input, InputNumber, Select, Button, DatePicker, TimePicker, Checkbox, AutoComplete, Typography, List, Space, Tag, Alert } from "antd";
import { EditOutlined, DeleteOutlined, UserAddOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useOuberService } from "../../../../services";
import AdminLinkTripInviteModal from "./AdminLinkTripInviteModal";
import AdminEditRequestModal from "./AdminEditRequestModal";
import AdminConfirmRemovalModal from "./AdminConfirmRemovalModal";
import { showErrorToast } from "../../ToastNotification";

const { Option } = Select;
const { Text } = Typography;

const getStatusTagClass = (status) => {
  switch (status) {
    case "Approved by AO": return "tag-approved";
    case "Requested to Join": return "tag-pending";
    case "Declined by AO":
    case "Cancelled by AO":
    case "Cancelled by User": return "tag-cancelled";
    default: return "";
  }
};

const AdminLinkTripModal = ({ open, onCancel, onSave, requestData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { service: ouberService } = useOuberService();
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isEditRequestOpen, setIsEditRequestOpen] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);

  const [existingVenues, setExistingVenues] = useState([
    { value: 'UPOU HQ' },
    { value: 'UP Los Baños' },
    { value: 'Crossing LB' },
    { value: 'Olivarez LB' },
    { value: 'UP Diliman' },
    { value: 'UP Manila' },
    { value: 'UP BGC' },
    { value: 'CHED Main Office' },
    { value: 'UPOU Learning Center Manila' },
    { value: 'NAIA Terminal 1' },
    { value: 'NAIA Terminal 2' },
    { value: 'NAIA Terminal 3' },
    { value: 'NAIA Terminal 4' },
    { value: 'PITX' },
    { value: 'Clark International Airport' },
  ]);

  const ouberServiceRef = useRef(ouberService);
  useEffect(() => {
    ouberServiceRef.current = ouberService;
  }, [ouberService]);

  useEffect(() => {
    if (open) {
      setLoading(false);
    }
    if (open && requestData) {
      // Set form values
      form.setFieldsValue({
        pickup: requestData.pickup_venue,
        destination: requestData.dropoff_location,
        date: requestData.trip_date ? dayjs(requestData.trip_date) : null,
        time: requestData.estimated_pickup_time ? dayjs(requestData.estimated_pickup_time, "HH:mm:ss") : null,
        purpose: requestData.trip_purpose || "Official Business",
        capacity: 1,
        isMultiplePickupAllowed: false,
        deadlineDate: requestData.trip_date ? dayjs(requestData.trip_date).subtract(1, 'day') : null,
        deadlineTime: dayjs("11:59 pm", "h:mm a"),
      });

      // Initialize passengers with the requestor
      const requestorName = requestData.external_passenger_name || (requestData.employee ? `${requestData.employee.first_name} ${requestData.employee.last_name}` : "Passenger");
      const requestor = {
        id: requestData.employee_id || `req_${requestData.id}`,
        trip_request_id: requestData.id || requestData.trip_request_id,
        employee_id: requestData.employee_id,
        name: requestorName,
        status: 'Approved by AO',
        time: requestData.estimated_pickup_time ? dayjs(requestData.estimated_pickup_time, "HH:mm:ss").format("h:mm a") : null,
        pickup_venue: requestData.pickup_venue,
        is_external: !!requestData.external_passenger_name,
        is_requestor: true
      };
      setPassengers([requestor]);
    } else if (open) {
      form.resetFields();
      setPassengers([]);
    }
    
    if (open) {
      const fetchRegistries = async () => {
        try {
          const drvs = await ouberServiceRef.current.getDrivers();
          const vehs = await ouberServiceRef.current.getVehicles();
          const trps = await ouberServiceRef.current.getTrips();
          setDrivers(Array.isArray(drvs) ? drvs : (drvs?.data || []));
          setVehicles(Array.isArray(vehs) ? vehs : (vehs?.data || []));

          const tripsList = Array.isArray(trps) ? trps : (trps?.data || []);
          const venuesFromTrips = [...new Set(tripsList.map(t => t.pickup_venue).filter(Boolean))];
          if (venuesFromTrips.length > 0) {
            setExistingVenues(venuesFromTrips.map(v => ({ value: v })));
          }
        } catch (error) {
          console.error("Failed to fetch drivers or vehicles", error);
        }
      };
      fetchRegistries();
    }
  }, [open, requestData, form]);

  if (!requestData) return null;

  const sanitizeInput = (val) => {
    if (typeof val !== 'string') return val;
    return val.trim().replace(/\s+/g, ' ');
  };

  const handleSubmit = async (values) => {
    const formattedValues = {
      trip_date: values.date ? values.date.format("YYYY-MM-DD") : null,
      pickup_time: values.time ? values.time.format("HH:mm:ss") : null,
      estimated_arrival_time: values.estimatedArrival ? values.estimatedArrival.format("HH:mm:ss") : null,
      pickup_venue: sanitizeInput(values.pickup),
      dropoff_location: sanitizeInput(values.destination),
      allowed_multiple_pickup: values.isMultiplePickupAllowed ? 1 : 0,
      trip_passenger_capacity: values.capacity,
      trip_purpose: sanitizeInput(values.purpose),
      remarks: sanitizeInput(values.purpose),
      driver_id: values.isCustomDriver ? null : (values.driver_id || null), 
      vehicle_id: values.isCustomVehicle ? null : (values.vehicle_id || null), 
      custom_driver_name: values.isCustomDriver ? sanitizeInput(values.custom_driver_name) : null,
      custom_driver_contact: values.isCustomDriver ? sanitizeInput(values.customNumber) : null,
      custom_vehicle_name: values.isCustomVehicle ? sanitizeInput(values.custom_vehicle_name) : null,
      custom_vehicle_plate: values.isCustomVehicle ? sanitizeInput(values.custom_vehicle_plate) : null,
      custom_vehicle_type: values.isCustomVehicle ? sanitizeInput(values.vehicleType) : null,
      linked_trip_request_id: requestData?.trip_request_id || requestData?.id,
      passengers: passengers.map(p => ({
        ...p,
        name: sanitizeInput(p.name),
        pickup_venue: sanitizeInput(p.pickup_venue)
      })),
    };
    
    setLoading(true);
    try {
      if (onSave) {
        await onSave(formattedValues);
      }
      form.resetFields();
    } catch (e) {
      // Parent handles
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditPassenger = (passenger) => {
    setSelectedPassenger(passenger);
    setIsEditRequestOpen(true);
  };

  const handleOpenRemovePassenger = (passenger) => {
    if (passenger.is_requestor) {
      showErrorToast("Error", "You cannot remove the primary requestor.");
      return;
    }
    setSelectedPassenger(passenger);
    setIsConfirmRemoveOpen(true);
  };

  const sortedPassengers = [
    ...passengers.filter(p => p.is_requestor),
    ...passengers.filter(p => !p.is_requestor).sort((a,b) => (a.time || "").localeCompare(b.time || ""))
  ];

  return (
    <>
    <Modal
      open={open}
      title="Link Trip Request to New Trip"
      onCancel={onCancel}
      footer={null}
      className="ouber-modal white-close-icon"
      width={700}
      zIndex={1060}
    >
      <div style={{ marginTop: "20px", maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px' }}>
        <Alert 
          message="Note: These details are based on the original request. You can edit the trip details anytime after creation if adjustments are needed." 
          type="info" 
          showIcon 
          style={{ marginBottom: '20px' }}
        />
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSubmit}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item name="date" label="Trip Date" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
              <DatePicker style={{ width: '100%' }} disabled />
            </Form.Item>
            <Form.Item name="capacity" label="Passenger Capacity" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }, { type: 'number', min: 1, message: "Must be at least 1" }]}>
              <InputNumber 
                min={1} 
                precision={0}
                style={{ width: '100%' }} 
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item name="time" label="Pickup Time" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
              <TimePicker use12Hours format="h:mm a" style={{ width: '100%' }} disabled />
            </Form.Item>
            <Form.Item 
              noStyle
              shouldUpdate={(prev, curr) => prev.time !== curr.time}
            >
              {({ getFieldValue }) => (
                <Form.Item 
                  name="estimatedArrival" 
                  label="Estimated Arrival" 
                  style={{ flex: 1 }} 
                  rules={[{ required: true, message: "Input cannot be empty" }]}
                >
                  <TimePicker 
                    use12Hours 
                    format="h:mm a" 
                    style={{ width: '100%' }} 
                    disabledTime={() => {
                      const pickup = getFieldValue('time');
                      if (!pickup) return {};
                      return {
                        disabledHours: () => Array.from({ length: pickup.hour() }, (_, i) => i),
                        disabledMinutes: (h) => h === pickup.hour() ? Array.from({ length: pickup.minute() }, (_, i) => i) : [],
                      };
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item 
              noStyle
              shouldUpdate={(prev, curr) => prev.date !== curr.date}
            >
              {({ getFieldValue }) => (
                <Form.Item 
                  name="deadlineDate" 
                  label="Deadline Date" 
                  style={{ flex: 1 }} 
                  rules={[
                    { required: true, message: "Input cannot be empty" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const tripDate = getFieldValue('date');
                        if (!value || !tripDate || value.isBefore(tripDate, 'day') || value.isSame(tripDate, 'day')) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Deadline cannot be after the trip date"));
                      },
                    }),
                  ]}
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    disabledDate={(current) => {
                      const tripDate = getFieldValue('date');
                      if (!tripDate) return current && current < dayjs().startOf('day');
                      return current && (current.isAfter(tripDate, 'day') || current < dayjs().startOf('day'));
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
            
            <Form.Item 
              noStyle
              shouldUpdate={(prev, curr) => prev.deadlineDate !== curr.deadlineDate || prev.time !== curr.time || prev.date !== curr.date}
            >
              {({ getFieldValue }) => (
                <Form.Item 
                  name="deadlineTime" 
                  label="Deadline Time" 
                  style={{ flex: 1 }} 
                  dependencies={['deadlineDate', 'date', 'time']}
                  rules={[
                    { required: true, message: "Input cannot be empty" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const deadlineDate = getFieldValue('deadlineDate');
                        const tripDate = getFieldValue('date');
                        const pickupTime = getFieldValue('time');
                        
                        if (!value || !deadlineDate || !tripDate || !pickupTime) {
                          return Promise.resolve();
                        }
                        
                        if (deadlineDate.isSame(tripDate, 'day')) {
                          const dMin = value.hour() * 60 + value.minute();
                          const pMin = pickupTime.hour() * 60 + pickupTime.minute();
                          
                          if (dMin >= pMin) {
                            return Promise.reject(new Error("Deadline must be before pickup time on the same date"));
                          }
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <TimePicker 
                    use12Hours 
                    format="h:mm a" 
                    style={{ width: '100%' }} 
                    disabledTime={() => {
                      const dDate = getFieldValue('deadlineDate');
                      const tDate = getFieldValue('date');
                      const pickup = getFieldValue('time');
                      if (!dDate || !tDate || !pickup || !dDate.isSame(tDate, 'day')) return {};
                      return {
                        disabledHours: () => {
                          const hours = [];
                          const startHour = pickup.minute() === 0 ? pickup.hour() : pickup.hour() + 1;
                          for (let i = startHour; i < 24; i++) hours.push(i);
                          return hours;
                        },
                        disabledMinutes: (h) => {
                          if (h === pickup.hour()) {
                            const mins = [];
                            for (let i = pickup.minute(); i < 60; i++) mins.push(i);
                            return mins;
                          }
                          return [];
                        }
                      };
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Form.Item name="isCustomDriver" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Driver not in registry?</Checkbox>
            </Form.Item>
          </div>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isCustomDriver !== currentValues.isCustomDriver}
          >
            {({ getFieldValue }) => {
              const isCustomDriver = getFieldValue('isCustomDriver');
              return isCustomDriver ? (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Form.Item name="custom_driver_name" label="Driver's Name" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                    <Input placeholder="Enter driver name" />
                  </Form.Item>
                  <Form.Item 
                    name="customNumber" 
                    label="Contact Number" 
                    style={{ flex: 1 }}
                    rules={[
                      { required: true, message: "Input cannot be empty" }, 
                      { pattern: /^09\d{9}$/, message: "Must be 11 digits and start with 09" }
                    ]}
                  >
                    <Input placeholder="E.g. 09123456789" maxLength={11} />
                  </Form.Item>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Form.Item name="driver_id" label="Driver's Name" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                    <Select 
                      showSearch
                      placeholder="Select a driver..."
                      optionFilterProp="children"
                      onChange={(val) => {
                        const selectedDriver = drivers.find(d => d.driver_id === val);
                        if (selectedDriver) {
                          form.setFieldsValue({ contactNumber: selectedDriver.contact_number });
                        }
                      }}
                    >
                      {drivers.sort((a, b) => a.driver_name.localeCompare(b.driver_name)).map(d => (
                        <Option key={d.driver_id} value={d.driver_id}>{d.driver_name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item 
                    name="contactNumber" 
                    label="Contact Number" 
                    style={{ flex: 1 }}
                    rules={[
                      { required: true, message: "Input cannot be empty" }, 
                      { pattern: /^09\d{9}$/, message: "Must be 11 digits and start with 09" }
                    ]}
                  >
                    <Input placeholder="E.g. 09123456789" maxLength={11} disabled />
                  </Form.Item>
                </div>
              );
            }}
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Form.Item name="isCustomVehicle" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Vehicle not in registry?</Checkbox>
            </Form.Item>
          </div>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isCustomVehicle !== currentValues.isCustomVehicle}
          >
            {({ getFieldValue }) => {
              const isCustomVehicle = getFieldValue('isCustomVehicle');
              return isCustomVehicle ? (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Form.Item name="custom_vehicle_name" label="Vehicle Name" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                    <Input placeholder="Enter vehicle name" />
                  </Form.Item>
                  <Form.Item name="custom_vehicle_plate" label="Plate Number" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                    <Input placeholder="Enter plate number" />
                  </Form.Item>
                  <Form.Item name="vehicleType" label="Vehicle Type" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                    <Select>
                      <Option value="University Owned">University Owned</Option>
                      <Option value="Car Rental">Car Rental</Option>
                      <Option value="Carpool">Carpool</Option>
                      <Option value="Outsourced">Outsourced</Option>
                    </Select>
                  </Form.Item>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Form.Item name="vehicle_id" label="Vehicle" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                    <Select 
                      showSearch
                      placeholder="Select a vehicle..."
                      optionFilterProp="children"
                      onChange={(val) => {
                        const selectedVehicle = vehicles.find(v => v.vehicle_id === val);
                        if (selectedVehicle) {
                          form.setFieldsValue({ 
                            vehicleType: selectedVehicle.vehicle_type,
                            capacity: selectedVehicle.max_passenger_capacity
                          });
                        }
                      }}
                    >
                      {vehicles.sort((a, b) => a.vehicle_model.localeCompare(b.vehicle_model)).map(v => (
                        <Option key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_model} ({v.plate_number})</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="vehicleType" label="Vehicle Type" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                    <Select disabled>
                      <Option value="University Owned">University Owned</Option>
                      <Option value="Car Rental">Car Rental</Option>
                      <Option value="Carpool">Carpool</Option>
                      <Option value="Outsourced">Outsourced</Option>
                    </Select>
                  </Form.Item>
                </div>
              );
            }}
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
            <Form.Item name="pickup" label="Pickup Venue" style={{ flex: 1, marginBottom: 0 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
              <AutoComplete options={existingVenues} placeholder="Pickup venue..." disabled />
            </Form.Item>
            <Form.Item name="destination" label="Dropoff Location" style={{ flex: 1, marginBottom: 0 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
              <AutoComplete options={existingVenues} placeholder="Dropoff location" disabled />
            </Form.Item>
          </div>
          
          <Form.Item name="isMultiplePickupAllowed" valuePropName="checked" style={{ marginBottom: '16px' }}>
            <Checkbox>Allow Multiple Pickup Venues</Checkbox>
          </Form.Item>

          <Form.Item name="purpose" label="Purpose of Trip" rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
            <Input.TextArea rows={3} placeholder="Purpose" disabled />
          </Form.Item>

          <div style={{ marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <Text strong>Passengers</Text>
              <Button 
                type="dashed" 
                icon={<UserAddOutlined />} 
                onClick={() => setIsInviteOpen(true)}
              >
                Invite Passengers
              </Button>
            </div>

            <List
              size="small"
              bordered
              dataSource={sortedPassengers}
              renderItem={passenger => (
                <List.Item
                  actions={passenger.is_requestor ? [] : [
                    !['Cancelled by AO', 'Cancelled by User', 'Declined by AO'].includes(passenger.status) && <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenEditPassenger(passenger)} />,
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleOpenRemovePassenger(passenger)} />
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    title={
                      <>
                        {passenger.name} 
                        {passenger.is_requestor && <Tag color="gold" style={{ border: 'none', marginLeft: '8px', fontSize: '10px' }}>Requestor</Tag>}
                        {!passenger.is_requestor && (
                          <Tag className={getStatusTagClass(passenger.status)} style={{ border: 'none', marginLeft: '8px', fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>
                            {passenger.status}
                          </Tag>
                        )}
                      </>
                    }
                    description={
                      <Space style={{ fontSize: '12px' }}>
                        <span><ClockCircleOutlined /> {passenger.time || "N/A"}</span>
                        {form.getFieldValue('isMultiplePickupAllowed') && passenger.pickup_venue && (
                          <span>• {passenger.pickup_venue}</span>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
            <Button onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button type="primary" htmlType="submit" className="ouber-btn-forest" loading={loading}>
              Create Connected Trip
            </Button>
          </div>
        </Form>
      </div>
    </Modal>

    <AdminLinkTripInviteModal 
      open={isInviteOpen}
      onCancel={() => setIsInviteOpen(false)}
      onAddEmployees={(emps) => {
        const formattedEmps = emps.map(emp => ({
          ...emp,
          id: emp.id,
          employee_id: emp.is_external ? null : emp.id,
          name: emp.name || `${emp.first_name} ${emp.last_name}`,
          status: 'Approved by AO',
          time: null,
          pickup_venue: form.getFieldValue('pickup') || 'UPOU HQ',
          is_external: !!emp.is_external
        }));
        setPassengers(prev => [...prev, ...formattedEmps]);
        setIsInviteOpen(false);
      }}
      maxSelectable={form.getFieldValue('capacity') ? Math.max(0, Number(form.getFieldValue('capacity')) - passengers.length) : null}
      existingPassengerIds={passengers.map(p => p.id || p.employee_id)}
    />

    <AdminEditRequestModal 
      open={isEditRequestOpen}
      onCancel={() => setIsEditRequestOpen(false)}
      passengerData={selectedPassenger}
      isMultiplePickupAllowed={form.getFieldValue('isMultiplePickupAllowed')}
      onSave={(updatedData) => {
        setPassengers(passengers.map(p => {
          if ((p.id || p.employee_id) === (updatedData.id || updatedData.employee_id)) {
            return {
              ...p,
              ...updatedData,
              status: updatedData.request_status,
              time: updatedData.estimated_pickup_time ? dayjs(updatedData.estimated_pickup_time, "HH:mm:ss").format("h:mm a") : p.time,
              pickup_venue: updatedData.pickup_venue
            };
          }
          return p;
        }));
        setIsEditRequestOpen(false);
      }}
    />

    <AdminConfirmRemovalModal 
      open={isConfirmRemoveOpen}
      onCancel={() => setIsConfirmRemoveOpen(false)}
      passengerData={selectedPassenger}
      onConfirm={(data) => {
        setPassengers(passengers.filter(p => (p.id || p.employee_id) !== (data.id || data.employee_id)));
        setIsConfirmRemoveOpen(false);
      }}
    />
    </>
  );
};

export default AdminLinkTripModal;
